import type { IObservableArray } from 'mobx';

import type { DisplayCreative, TChannel } from '@feathr/blackbox';
import { EIntegrationTypes } from '@feathr/blackbox';

/**
 * Parameters every creative-flow hook accepts. Channel-specific hooks
 * (image/video, ad tag, Bannersnack) extend this with their own extra props.
 */
export interface ICreativeFlowBaseParams {
  /** Campaign ID to associate the creative with. If omitted, created at the event level. */
  campaignId?: string;
  /** Event ID to associate the creative with when no campaign is provided. */
  eventId?: string;
  /** Observable array of creatives for optimistic updates. */
  creatives?: IObservableArray<DisplayCreative>;
  /** Callback to trigger an appear animation after adding to the list. */
  onAppear?: (id: string) => void;
  /** Callback when the flow completes successfully. */
  onComplete: () => void;
  /** Callback when the user clicks Back. */
  onBack: () => void;
  /** Callback when the user cancels. */
  onCancel: () => void;
}

/** Upload state machine states */
export type TUploadState = 'idle' | 'uploading' | 'processing';

/** Result of processing a single uploaded file */
export interface IUploadResult {
  /** Original filename */
  filename: string;
  /** Whether the upload was successful */
  success: boolean;
  /** Error message if failed */
  error?: string;
  /** URL of the uploaded file (for thumbnail display) */
  url?: string;
}

/** Creative type options for Display channel */
export type TDisplayCreativeType = 'image_video' | 'ad_tag' | 'build' | 'duplicate';

/** Creative type options for Meta channel */
export type TMetaCreativeType = 'image_video' | 'build' | 'duplicate';

/** Union of all creative types */
export type TCreativeType = TDisplayCreativeType | TMetaCreativeType;

/**
 * Converts an integration type to its corresponding channel.
 *
 * @param integration - The integration type to convert
 * @returns The corresponding channel, or undefined if not a channel-related integration
 */
export function integrationToChannel(integration: EIntegrationTypes): TChannel | undefined {
  switch (integration) {
    case EIntegrationTypes.Tradedesk:
      return 'display';

    case EIntegrationTypes.Meta:
      return 'meta';

    default:
      return undefined;
  }
}

/**
 * Converts an array of integration types to their corresponding channels.
 *
 * @param integrations - The integration types to convert (filters out non-channel integrations)
 * @returns Array of channels (only includes valid channel integrations)
 */
export function integrationsToChannels(integrations: EIntegrationTypes[] | undefined): TChannel[] {
  if (!integrations) {
    return [];
  }
  return integrations
    .map(integrationToChannel)
    .filter((channel): channel is TChannel => channel !== undefined);
}
